import React from 'react';
import { ExternalLink, MessageCircle, ShieldCheck, Smartphone, Link2 } from 'lucide-react';
import { PORTAL_SHELL } from '../portalTheme';

export const ELEMENT_DOWNLOAD_URL = 'https://element.io/download';

/**
 * Element-Web UI-Origin (Raum-Deep-Links).
 * P3 (Firma): self-hosted auf `chat.arkorbit.at` — Homeserver bleibt `matrix.arkorbit.at` (Synapse).
 * P4: dieser Wert ist die einzige Schaltstelle für Portal-Links (MatrixView + DiktatView).
 */
export const ELEMENT_WEB_ORIGIN = 'https://chat.arkorbit.at';

/** Element-Web Deep-Link für einen Matrix-Raum (PB-Feld matrixRoomId). */
export function buildElementRoomUrl(matrixRoomId: string): string {
  const id = matrixRoomId.trim();
  if (!id) return '';
  if (/^https?:\/\//i.test(id)) return id;
  const base = ELEMENT_WEB_ORIGIN.replace(/\/$/, '');
  return `${base}/#/room/${encodeURIComponent(id)}`;
}

export interface MatrixViewProps {
  matrixRoomId: string;
  onOpenDiktatAnleitung: () => void;
}

export const MatrixView: React.FC<MatrixViewProps> = ({ matrixRoomId, onOpenDiktatAnleitung }) => {
  const roomUrl = buildElementRoomUrl(matrixRoomId);
  const hasRoom = Boolean(roomUrl);

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl mx-auto">
      <header>
        <h1 className="text-xl font-semibold text-white tracking-tight">Matrix</h1>
        <p className={`${PORTAL_SHELL.textMuted} text-sm mt-1`}>
          Verschlüsselter Kanzlei-Raum für Diktat und Protokolle — Self-Hosted in Frankfurt.
        </p>
      </header>

      {hasRoom ? (
        <section className={`${PORTAL_SHELL.ingestBar} p-5 space-y-4`}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-5 h-5 text-emerald-400" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-white text-sm font-medium">Kanzlei-Raum</h2>
              <p className="text-slate-400 text-sm mt-1 leading-relaxed">
                Sprachnachrichten und Protokoll-Links laufen Ende-zu-Ende verschlüsselt über Element.
              </p>
            </div>
          </div>
          <a
            href={roomUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors"
          >
            <ExternalLink className="w-4 h-4" aria-hidden />
            Im Browser öffnen (Element)
          </a>
          <div className="flex items-start gap-2 text-xs text-slate-500 leading-relaxed">
            <ShieldCheck className="w-4 h-4 text-emerald-500/80 flex-shrink-0 mt-0.5" aria-hidden />
            E2EE · matrix.arkorbit.at · EU
          </div>
        </section>
      ) : (
        <section className={`${PORTAL_SHELL.ingestBar} p-5 space-y-3`}>
          <p className="text-slate-400 text-sm leading-relaxed">
            Für Ihren Zugang ist noch kein Matrix-Raum hinterlegt. ARKORBIT richtet den verschlüsselten
            Kanzlei-Raum beim Onboarding ein — bis dahin finden Sie die Element-Anleitung unter Diktat.
          </p>
          <button
            type="button"
            onClick={onOpenDiktatAnleitung}
            className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 underline"
          >
            <Smartphone className="w-4 h-4" aria-hidden />
            Zur Element-Anleitung
          </button>
        </section>
      )}

      <section className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Kurzüberblick</h2>
        <div className="grid gap-2 text-sm text-slate-400">
          <p className="flex items-start gap-2">
            <Smartphone className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" aria-hidden />
            Element App installieren —{' '}
            <a
              href={ELEMENT_DOWNLOAD_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300"
            >
              <Link2 className="w-3.5 h-3.5" aria-hidden />
              element.io/download
            </a>
          </p>
          <p className="flex items-start gap-2">
            <MessageCircle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" aria-hidden />
            Sprachnachricht im Raum senden → Transkription und PDF-Protokoll folgen automatisch.
          </p>
          <p className="flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" aria-hidden />
            Protokolle zusätzlich unter Dokumente im Portal abrufbar.
          </p>
        </div>
      </section>
    </div>
  );
};
